import {
	AMERICAN_MORTGAGE_INTEREST_RATE,
	AMERICAN_MORTGAGE_LIMITS,
} from "shared/CalculatorLimits";
import { getAmericanMortgagePlan } from "shared/getAmericanMortgagePlan";
import { CalculatorRateAmount } from "web_myplann/Calculators/Components/CalculatorRateAmount";
import { CalculatorResultSection } from "web_myplann/Calculators/Components/CalculatorResultSection";
import { CalculatorShell } from "web_myplann/Calculators/Components/CalculatorShell";
import {
	AMERICAN_MORTGAGE_DEFAULT_LOAN_VALUE,
	AMERICAN_MORTGAGE_DEFAULT_YEARS,
} from "web_myplann/Calculators/constants";
import { getAmericanMortgageFormSchema } from "web_myplann/Calculators/Forms/americanMortgageFormSchema";
import { useCalculatorForm } from "web_myplann/Calculators/Hooks/useCalculatorForm";
import { useCalculatorLead } from "web_myplann/Calculators/Hooks/useCalculatorLead";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	formatCurrency,
	getCurrencySymbol,
} from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { SliderFormRow } from "web_myplann/Shared/Components/SliderFormRow";
import { SliderRow } from "web_myplann/Shared/Components/SliderRow";
import { CurrencyCode } from "web_myplann/Shared/constants";

export const AmericanMortgageCalculatorPage = () => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const { send, ...lead } = useCalculatorLead(CalculatorType.AmericanMortgage);
	const { control, watch, setValue, handleSubmit } = useCalculatorForm(
		getAmericanMortgageFormSchema,
		{
			years: AMERICAN_MORTGAGE_DEFAULT_YEARS,
			loanValue: AMERICAN_MORTGAGE_DEFAULT_LOAN_VALUE,
		},
	);

	const { propertyPrice, years, loanValue } = watch();
	const { hasValidPrice, maxLoanValue, actualLoanValue, monthlyPayment } =
		getAmericanMortgagePlan({
			propertyPrice,
			loanValue,
			years,
			rounding: "floor",
		});
	const sliderValue = hasValidPrice
		? actualLoanValue
		: AMERICAN_MORTGAGE_LIMITS.loanMin;
	const sliderMax = hasValidPrice
		? maxLoanValue
		: AMERICAN_MORTGAGE_LIMITS.loanMin;

	const onSubmit = handleSubmit((values) =>
		send({
			amount: actualLoanValue,
			installment: monthlyPayment,
			interest: AMERICAN_MORTGAGE_INTEREST_RATE,
			period: values.years,
			propertyValue: values.propertyPrice,
		}),
	);

	return (
		<CalculatorShell
			type={CalculatorType.AmericanMortgage}
			onSubmit={onSubmit}
			fields={
				<>
					<FlatRowFormInput
						control={control}
						name="propertyPrice"
						label={t(Resources.Calculators.AmericanMortgage.PropertyPrice)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={AMERICAN_MORTGAGE_LIMITS.propertyPriceMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<SliderRow
						label={t(Resources.Calculators.AmericanMortgage.LoanValue)}
						valueLabel={formatCurrency(sliderValue, CurrencyCode.CZK, 0)}
						value={sliderValue}
						onChange={(value) => setValue("loanValue", value)}
						min={AMERICAN_MORTGAGE_LIMITS.loanMin}
						max={sliderMax}
						step={AMERICAN_MORTGAGE_LIMITS.loanStep}
						minLabel={formatCurrency(
							AMERICAN_MORTGAGE_LIMITS.loanMin,
							CurrencyCode.CZK,
							0,
						)}
						maxLabel={formatCurrency(sliderMax, CurrencyCode.CZK, 0)}
						disabled={!hasValidPrice}
					/>
					<SliderFormRow
						control={control}
						name="years"
						label={t(Resources.Calculators.AmericanMortgage.Years)}
						formatValue={yearsLabel}
						min={AMERICAN_MORTGAGE_LIMITS.yearsMin}
						max={AMERICAN_MORTGAGE_LIMITS.yearsMax}
						disabled={!hasValidPrice}
						isLast
					/>
				</>
			}
			result={
				monthlyPayment > 0 && (
					<CalculatorResultSection
						badge={t(Resources.Calculators.Result.Badges.monthlyInstalment)}
						hero={
							<CalculatorRateAmount
								amount={monthlyPayment}
								rate={AMERICAN_MORTGAGE_INTEREST_RATE}
							/>
						}
						rows={[
							{
								label: t(
									Resources.Calculators.AmericanMortgage.ResultLoanValue,
								),
								value: formatCurrency(actualLoanValue, CurrencyCode.CZK, 0),
							},
						]}
						lead={lead}
					/>
				)
			}
		/>
	);
};
